import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";
import { displayCompanyName } from "@/lib/displayCompanyName";

export interface LoadDetailData {
  load: any | null;
  loadStops: any[];
  dispatch: any | null;
  dispatchStops: any[];
  documents: any[];
  events: any[];
  bids: any[];
  awardedBroker: { user_id: string | null; company_name: string | null; contact_name: string | null; email: string | null; phone: string | null } | null;
}

async function fetchLoadDetail(loadId: string): Promise<LoadDetailData> {
  // 1. Load + stops + dispatch in parallel
  const [loadRes, loadStopsRes, dispatchRes, bidsRes] = await Promise.all([
    supabase.from("direct_loads" as any).select("*").eq("id", loadId).maybeSingle(),
    supabase.from("direct_load_stops" as any).select("*").eq("direct_load_id", loadId).order("stop_number", { ascending: true }),
    supabase.from("direct_dispatches" as any).select("*").eq("direct_load_id", loadId).maybeSingle(),
    supabase.from("direct_bids" as any).select("*").eq("direct_load_id", loadId).order("bid_amount", { ascending: true }),
  ]);

  const load: any = loadRes.data ?? null;
  const dispatch: any = dispatchRes.data ?? null;
  const bidsRaw: any[] = (bidsRes.data as any[]) ?? [];

  // 2. Hydrate broker company names on bids
  const brokerCompanyIds = Array.from(new Set(bidsRaw.map((b) => b.broker_company_id).filter(Boolean)));
  let companyMap = new Map<string, string>();
  if (brokerCompanyIds.length > 0) {
    const { data: comps } = await supabase
      .from("companies")
      .select("id, name")
      .in("id", brokerCompanyIds);
    (comps ?? []).forEach((c: any) => companyMap.set(c.id, c.name));
  }
  const bids = bidsRaw.map((b) => ({ ...b, broker_company_name: displayCompanyName(companyMap.get(b.broker_company_id)) || "Broker" }));

  // 3. Dispatch-scoped data (only if dispatch exists)
  let dispatchStops: any[] = [];
  let documents: any[] = [];
  let events: any[] = [];
  if (dispatch?.id) {
    const [stopsRes, docsRes, evRes] = await Promise.all([
      supabase.from("direct_dispatch_stops" as any).select("*").eq("dispatch_id", dispatch.id).order("stop_number", { ascending: true }),
      supabase.from("direct_dispatch_documents" as any).select("id, dispatch_id, uploaded_by, doc_type, file_path, file_name, file_size, mime_type, created_at").eq("dispatch_id", dispatch.id).order("created_at", { ascending: false }),
      supabase.from("direct_dispatch_events" as any).select("id, dispatch_id, actor_id, event_type, payload, created_at").eq("dispatch_id", dispatch.id).order("created_at", { ascending: false }).limit(200),
    ]);
    dispatchStops = (stopsRes.data as any[]) ?? [];
    documents = (docsRes.data as any[]) ?? [];
    events = (evRes.data as any[]) ?? [];

    // Hydrate uploader and actor names
    const userIds = Array.from(new Set([
      ...documents.map((d) => d.uploaded_by),
      ...events.map((e) => e.actor_id),
    ].filter(Boolean)));
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", userIds);
      const nameMap = new Map<string, string>();
      (profs ?? []).forEach((p: any) => {
        nameMap.set(p.id, [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "User");
      });
      documents = documents.map((d) => ({ ...d, uploader_name: nameMap.get(d.uploaded_by) ?? "User" }));
      events = events.map((e) => ({ ...e, actor_name: e.actor_id ? (nameMap.get(e.actor_id) ?? "User") : "System" }));
    }
  }

  // 4. Awarded broker contact
  let awardedBroker: LoadDetailData["awardedBroker"] = null;
  if (load?.awarded_bid_id) {
    const winningBid = bids.find((b) => b.id === load.awarded_bid_id);
    if (winningBid) {
      const [profRes, compRes] = await Promise.all([
        winningBid.broker_id
          ? supabase.from("profiles").select("first_name, last_name, email, phone").eq("id", winningBid.broker_id).maybeSingle()
          : Promise.resolve({ data: null } as any),
        winningBid.broker_company_id
          ? supabase.from("companies").select("name").eq("id", winningBid.broker_company_id).maybeSingle()
          : Promise.resolve({ data: null } as any),
      ]);
      const prof: any = profRes.data;
      const comp: any = compRes.data;
      awardedBroker = {
        user_id: winningBid.broker_id ?? null,
        company_name: displayCompanyName(comp?.name) || winningBid.broker_company_name || null,
        contact_name: prof ? [prof.first_name, prof.last_name].filter(Boolean).join(" ") || prof.email : null,
        email: prof?.email ?? null,
        phone: prof?.phone ?? null,
      };
    }
  }

  return {
    load,
    loadStops: (loadStopsRes.data as any[]) ?? [],
    dispatch,
    dispatchStops,
    documents,
    events,
    bids,
    awardedBroker,
  };
}

export function useLoadDetail(loadId: string | undefined) {
  const query = useQuery({
    queryKey: ["lr-direct-load-detail", loadId],
    queryFn: () => fetchLoadDetail(loadId!),
    enabled: !!loadId,
    staleTime: 30_000,
  });

  // Realtime invalidations for dispatch row + child records. The direct_dispatches
  // subscription is keyed by loadId so the broker flips live when the shipper
  // marks the packet ready and `dispatch_prepared_at` changes.
  // Deps are intentionally primitive/stable (`refetch` is referentially stable in
  // react-query v5) — depending on the whole `query` object resubscribed the
  // channel on every render and lost realtime events.
  const dispatchIdForRealtime = query.data?.dispatch?.id ?? null;
  const refetch = query.refetch;
  useEffect(() => {
    if (!loadId) return;
    const dispatchId = dispatchIdForRealtime;
    let ch = supabase
      .channel(`load-detail-${loadId}-${dispatchId ?? "pending"}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_dispatches", filter: `direct_load_id=eq.${loadId}` }, () => { void refetch(); });

    if (dispatchId) {
      ch = ch
        .on("postgres_changes", { event: "*", schema: "public", table: "direct_dispatch_events", filter: `dispatch_id=eq.${dispatchId}` }, () => { void refetch(); })
        .on("postgres_changes", { event: "*", schema: "public", table: "direct_dispatch_documents", filter: `dispatch_id=eq.${dispatchId}` }, () => { void refetch(); })
        .on("postgres_changes", { event: "*", schema: "public", table: "direct_dispatch_stops", filter: `dispatch_id=eq.${dispatchId}` }, () => { void refetch(); });
    }

    ch.subscribe((status) => { if (status === "SUBSCRIBED") void refetch(); });
    return () => { supabase.removeChannel(ch); };
  }, [loadId, dispatchIdForRealtime, refetch]);

  useRealtimeSelfHeal(() => { void refetch(); }, !!loadId);

  return query;
}
